import { uploadToSignedUrl } from '@/data/storage'
import encryptImage from '@/utils/encrypt-image'
import resizeImageSW from '@/utils/resize-image-sw'

interface UploadImagesArgs {
  image: Blob
  fileName: string
  encryptKey: string
}

export const uploadImages = async ({
  image,
  fileName,
  encryptKey,
}: UploadImagesArgs): Promise<{
  original: string
  thumbnail: string
  type: string
}> => {
  // Resize original if too large and generate thumbnail
  const [resizedOriginal, thumbnail] = await Promise.all([
    resizeImageSW(image, 1200), // Max width for original
    resizeImageSW(image, 300), // Thumbnail size
  ])

  const resizedFile = new File([resizedOriginal], fileName, {
    type: image.type,
  })

  const thumbnailFile = new File([thumbnail], `thumb_${fileName}`, {
    type: image.type,
  })

  // Encrypt both versions
  const [encryptedImage, encryptedThumbnail] = await Promise.all([
    encryptImage(resizedFile, encryptKey),
    encryptImage(thumbnailFile, encryptKey),
  ])

  // Upload both files
  const [originalUpload, thumbnailUpload] = await Promise.all([
    uploadToSignedUrl({
      file: encryptedImage,
    }),
    uploadToSignedUrl({
      file: encryptedThumbnail,
    }),
  ])

  if (!originalUpload.data || !thumbnailUpload.data) {
    throw new Error(
      `Failed to upload: ${originalUpload.error?.message || thumbnailUpload.error?.message}`,
    )
  }

  return {
    original: originalUpload.data.path,
    thumbnail: thumbnailUpload.data.path,
    type: image.type,
  }
}
